import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { ChevronDown, Download, File, ImagePlus, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/utils/apiError";
import { formatDateTimeMdY } from "@/utils/dateFormat";
import {
  isPlaceholderImageUrl,
  realImageAssets,
  vehicleVideos,
} from "@/utils/vehicleImages";
import AdminDialog from "@/components/admin/AdminDialog";
import ImagineRegionEditor from "@/components/admin/ImagineRegionEditor";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const RESULTS_PAGE_SIZES = [25, 35, 50];
const DEFAULT_RESULTS_PAGE_SIZE = 25;

const inputCls =
  "w-full bg-white/5 border border-white/10 text-white px-3 py-2.5 font-['Manrope'] text-sm focus:outline-none focus:border-blue-500/50";
const selectCls = `${inputCls} [&>option]:bg-gray-900 [&>option]:text-white`;
const labelCls = "block text-xs font-['Manrope'] text-white/50 mb-1";
const mediaThumbCls = "relative w-28 h-28 sm:w-32 sm:h-32 overflow-hidden border";
const mediaTileCls =
  "w-28 h-28 sm:w-32 sm:h-32 border text-white/70 hover:border-blue-400 hover:text-white flex flex-col items-center justify-center gap-1 disabled:opacity-50";
const libraryThumbCls = "w-32 h-32 sm:w-36 sm:h-36 overflow-hidden border";
const libraryTileCls =
  "w-32 h-32 sm:w-36 sm:h-36 border border-dashed border-white/25 text-white/70 hover:border-blue-400 hover:text-white flex flex-col items-center justify-center gap-1 disabled:opacity-50";

function authHeaders(token) {
  return { Authorization: `Bearer ${token}` };
}

function statusLabel(status) {
  const s = String(status || "").replace(/_/g, " ");
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : "—";
}

function isTimeoutJob(job) {
  if (!job) return false;
  if (job.timeout_recoverable && job.status === "failed") return true;
  return job.status === "failed" && /timed out waiting for higgsfield/i.test(job.error || "");
}

function isWorkingJob(job) {
  if (!job) return false;
  if (job.status === "queued" || job.status === "in_progress" || isTimeoutJob(job)) return true;
  return job.status === "completed" && mediaItems(job).length === 0;
}

function isVideoMedia(item) {
  const type = item?.resource_type || item?.type || "";
  if (type === "video") return true;
  const url = String(item?.url || "").split("?")[0].toLowerCase();
  return url.endsWith(".mp4") || url.endsWith(".webm") || url.endsWith(".mov") || url.endsWith(".m4v");
}

function mediaItems(job) {
  const clouds = Array.isArray(job?.cloudinary_assets) ? job.cloudinary_assets : [];
  if (clouds.some((item) => item?.url)) {
    return clouds.filter((item) => item?.url).map((item) => ({
      url: item.url,
      resource_type: item.resource_type || "image",
      thumbnail_url: item.thumbnail_url,
    }));
  }
  const raw = Array.isArray(job?.result_urls) ? job.result_urls : [];
  return raw.filter((item) => item?.url).map((item) => ({
    url: item.url,
    resource_type: item.type || item.resource_type || "image",
    thumbnail_url: item.thumbnail_url,
  }));
}

function workingLabel(job) {
  if (job?.kind === "generate_video") return "Generating video…";
  if (job?.kind === "fix_photo") return "Fixing photo…";
  if (job?.kind === "change_background") return "Changing background…";
  return "Generating photo…";
}

function jobKindLabel(job) {
  if (job?.kind === "generate_video") return "Generate video";
  if (job?.kind === "fix_photo") return "Fix photo";
  if (job?.kind === "change_background") return "Change background";
  if (job?.kind === "generate_photo") return "Generate photo";
  return job?.kind || "Imagine";
}

function modelLimit(model, key) {
  const value = Number(model?.[key]);
  return Number.isFinite(value) && value > 0 ? value : 0;
}

function referenceLimitHint(model, mode) {
  if (!model) return "Use listing photos or upload extra stills from this device as generation references.";
  const maxImages = modelLimit(model, "max_images");
  const minImages = modelLimit(model, "min_images");
  const maxVideos = modelLimit(model, "max_videos");
  const minVideos = modelLimit(model, "min_videos");
  const parts = [];
  if (maxImages <= 0) {
    parts.push("This model does not use reference photos.");
  } else if (minImages > 0 && minImages === maxImages) {
    parts.push(`Select ${maxImages} reference photo${maxImages === 1 ? "" : "s"}.`);
  } else if (minImages > 0) {
    parts.push(`Select ${minImages}–${maxImages} reference photos.`);
  } else {
    parts.push(`Select up to ${maxImages} reference photo${maxImages === 1 ? "" : "s"} (optional).`);
  }
  if (mode === "video" && maxVideos > 0) {
    if (minVideos > 0 && minVideos === maxVideos) {
      parts.push(`Select ${maxVideos} reference video${maxVideos === 1 ? "" : "s"}.`);
    } else if (minVideos > 0) {
      parts.push(`Select ${minVideos}–${maxVideos} reference videos.`);
    } else {
      parts.push(`Select up to ${maxVideos} reference video${maxVideos === 1 ? "" : "s"} (optional).`);
    }
  }
  return parts.join(" ");
}

function formatJobCost(job) {
  const estimate = job?.estimate && typeof job.estimate === "object" ? job.estimate : {};
  const usd = estimate.usd ?? job?.estimate_usd;
  const credits = estimate.credits ?? job?.estimate_credits;
  const usdNum = usd == null || usd === "" ? NaN : Number(usd);
  const parts = [];
  if (!Number.isNaN(usdNum)) {
    parts.push(`$${usdNum.toFixed(2)}`);
  } else if (usd != null && usd !== "") {
    parts.push(`$${usd}`);
  }
  if (credits != null && credits !== "") {
    parts.push(`${credits} credits`);
  }
  return parts.length ? parts.join(" · ") : "Unavailable";
}

function jobGeneratedParts(job) {
  const name = (job?.admin_name || "").trim();
  const role = (job?.admin_role_label || "").trim();
  const email = (job?.admin_email || "").trim();
  const combined = (job?.generated_by || "").trim();
  const personIsRole = Boolean(name && role && name.toLowerCase() === role.toLowerCase());
  let person = personIsRole ? email : name;
  if (!person && combined.includes(" · ")) {
    person = combined.split(" · ")[0].trim();
  }
  person = person || email || combined || "Unknown";
  let position = role;
  if (!position && combined.includes(" · ")) {
    position = combined.split(" · ").slice(1).join(" · ").trim();
  }
  if (position && person.toLowerCase() === position.toLowerCase()) {
    position = "";
  }
  return { name: person, role: position };
}

function mediaExtension(item) {
  if (isVideoMedia(item)) {
    const path = String(item?.url || "").split("?")[0].toLowerCase();
    if (path.endsWith(".webm")) return "webm";
    if (path.endsWith(".mov")) return "mov";
    if (path.endsWith(".m4v")) return "m4v";
    return "mp4";
  }
  const path = String(item?.url || "").split("?")[0].toLowerCase();
  const ext = path.split(".").pop();
  if (["jpg", "jpeg", "png", "webp", "gif"].includes(ext)) return ext === "jpeg" ? "jpg" : ext;
  return "jpg";
}

function downloadFilename(job, item, index, total) {
  const ext = mediaExtension(item);
  const kind = job?.kind === "generate_video" || isVideoMedia(item) ? "video" : "photo";
  const suffix = total > 1 ? `-${index + 1}` : "";
  const id = String(job?.id || "file").slice(0, 8);
  return `drivenow-imagine-${kind}-${id}${suffix}.${ext}`;
}

function mediaAttachmentUrl(url, filename) {
  const text = String(url || "");
  if (text.includes("res.cloudinary.com/") && text.includes("/upload/")) {
    const safe = encodeURIComponent(String(filename || "download").replace(/[^\w.\-]+/g, "_"));
    return text.replace("/upload/", `/upload/fl_attachment:${safe}/`);
  }
  return text;
}

async function downloadUrl(url, filename) {
  try {
    const response = await fetch(url, { mode: "cors", credentials: "omit" });
    if (!response.ok) throw new Error("fetch failed");
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(objectUrl);
    return true;
  } catch {
    const link = document.createElement("a");
    link.href = mediaAttachmentUrl(url, filename);
    link.download = filename;
    link.rel = "noopener noreferrer";
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    link.remove();
    return true;
  }
}

function jobReferencePreviewUrls(job) {
  const seen = new Set();
  const urls = [];
  const add = (url) => {
    const text = String(url || "").trim();
    if (!text || seen.has(text)) return;
    seen.add(text);
    urls.push(text);
  };
  (Array.isArray(job?.reference_image_urls) ? job.reference_image_urls : []).forEach(add);
  add(job?.first_frame_url);
  add(job?.last_frame_url);
  return urls;
}

function jobInMedia(job, vehicle) {
  if (!job || !vehicle) return false;
  const jobId = job.id;
  const clouds = Array.isArray(job.cloudinary_assets) ? job.cloudinary_assets : [];
  const cloudUrls = new Set(clouds.map((item) => item?.url).filter(Boolean));
  const cloudIds = new Set(clouds.map((item) => item?.public_id).filter(Boolean));
  const items = [...(vehicle.image_assets || []), ...(vehicle.video_assets || [])];
  for (const raw of items) {
    const item = typeof raw === "string" ? { url: raw } : raw || {};
    if (jobId && item.imagine_job_id === jobId) return true;
    if (item.url && cloudUrls.has(item.url)) return true;
    if (item.public_id && cloudIds.has(item.public_id)) return true;
  }
  return false;
}

function PhotoPreviewPicker({ id, label, photos, value, onChange, disabled, allowEmpty = false, emptyLabel = "None" }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const selectedIdx = photos.findIndex((photo) => photo.url === value);
  const selected = selectedIdx >= 0 ? photos[selectedIdx] : null;

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div ref={wrapRef} className="relative">
      {label && (
        <label className={labelCls} htmlFor={id}>{label}</label>
      )}
      <button
        id={id}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((cur) => !cur)}
        className={`${inputCls} flex items-center gap-2 text-left disabled:opacity-50`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {selected ? (
          <img src={selected.url} alt="" className="w-8 h-8 object-cover shrink-0 bg-black/40" />
        ) : (
          <span className="w-8 h-8 shrink-0 bg-white/10" />
        )}
        <span className="flex-1 truncate">
          {selected ? `Photo ${selectedIdx + 1}` : emptyLabel}
        </span>
        <ChevronDown className="w-4 h-4 text-white/50 shrink-0" />
      </button>
      {open && (
        <div
          className="absolute z-30 mt-1 w-full max-h-64 overflow-auto border border-white/15 bg-[#0a0a0a] p-1 shadow-xl"
          role="listbox"
        >
          {allowEmpty && (
            <button
              type="button"
              className="w-full flex items-center gap-2 px-2 py-1.5 text-left text-sm font-['Manrope'] text-white/70 hover:bg-white/5"
              onClick={() => {
                onChange("");
                setOpen(false);
              }}
            >
              <span className="w-10 h-10 shrink-0 bg-white/10" />
              {emptyLabel}
            </button>
          )}
          {photos.map((photo, idx) => {
            const on = photo.url === value;
            return (
              <button
                key={photo.public_id || photo.url}
                type="button"
                role="option"
                aria-selected={on}
                className={`w-full flex items-center gap-2 px-2 py-1.5 text-left text-sm font-['Manrope'] hover:bg-white/5 ${
                  on ? "bg-blue-500/15 text-white" : "text-white/80"
                }`}
                onClick={() => {
                  onChange(photo.url);
                  setOpen(false);
                }}
              >
                <img src={photo.url} alt="" className="w-10 h-10 object-cover shrink-0 bg-black/40" />
                Photo {idx + 1}
              </button>
            );
          })}
          {!photos.length && (
            <p className="px-2 py-2 text-xs font-['Manrope'] text-white/40">No photos available.</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function VehicleImagineSection({ vehicle, token, onApplied }) {
  const [models, setModels] = useState([]);
  const [configured, setConfigured] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [mode, setMode] = useState("photo");
  const [genModel, setGenModel] = useState("");
  const [genPrompt, setGenPrompt] = useState("");
  const [selectedImages, setSelectedImages] = useState([]);
  const [selectedVideos, setSelectedVideos] = useState([]);
  const [duration, setDuration] = useState(5);
  const [resolution, setResolution] = useState("");
  const [fixModel, setFixModel] = useState("");
  const [fixPrompt, setFixPrompt] = useState("");
  const [fixPhoto, setFixPhoto] = useState("");
  const [boxes, setBoxes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [fixBusy, setFixBusy] = useState(false);
  const [uploadedRefs, setUploadedRefs] = useState([]);
  const [firstFrameUrl, setFirstFrameUrl] = useState("");
  const [lastFrameUrl, setLastFrameUrl] = useState("");
  const [uploadingRefs, setUploadingRefs] = useState(false);
  const [applyingId, setApplyingId] = useState("");
  const [downloadingId, setDownloadingId] = useState("");
  const [infoJob, setInfoJob] = useState(null);
  const uploadRef = useRef(null);
  const bgUploadRef = useRef(null);
  const [bgModel, setBgModel] = useState("qwen-image-3-edit");
  const [bgPrompt, setBgPrompt] = useState("");
  const [bgSelectedImages, setBgSelectedImages] = useState([]);
  const [bgUploads, setBgUploads] = useState([]);
  const [bgBackgroundUrl, setBgBackgroundUrl] = useState("");
  const [bgBusy, setBgBusy] = useState(false);
  const [uploadingBg, setUploadingBg] = useState(false);
  const [bgLibraryOpen, setBgLibraryOpen] = useState(false);
  const [bgLibrary, setBgLibrary] = useState([]);
  const [uploadingLibrary, setUploadingLibrary] = useState(false);
  const [deletingBgId, setDeletingBgId] = useState("");
  const bgLibraryUploadRef = useRef(null);
  const [resultsPageSize, setResultsPageSize] = useState(DEFAULT_RESULTS_PAGE_SIZE);
  const [resultsPage, setResultsPage] = useState(1);

  const photos = useMemo(
    () =>
      realImageAssets(vehicle?.image_assets).filter((a) => !isPlaceholderImageUrl(a.url)),
    [vehicle],
  );
  const videos = useMemo(() => vehicleVideos(vehicle), [vehicle]);
  const referencePhotos = useMemo(() => {
    const seen = new Set();
    const rows = [];
    for (const photo of [...photos, ...uploadedRefs]) {
      if (!photo?.url || seen.has(photo.url) || isPlaceholderImageUrl(photo.url)) continue;
      seen.add(photo.url);
      rows.push(photo);
    }
    return rows;
  }, [photos, uploadedRefs]);

  const photoModels = useMemo(
    () => models.filter((m) => m.kind === "photo"),
    [models],
  );
  const videoModels = useMemo(
    () => models.filter((m) => m.kind === "video" || m.kind === "video-edit"),
    [models],
  );
  const editModels = useMemo(
    () => models.filter((m) => m.kind === "photo-edit"),
    [models],
  );
  const imageModels = useMemo(
    () => models.filter((m) => m.output === "image"),
    [models],
  );
  const activeBg = imageModels.find((m) => m.id === bgModel) || imageModels[0];
  const genModels = mode === "video" ? videoModels : photoModels;
  const activeGen = genModels.find((m) => m.id === genModel) || genModels[0];
  const supportsFrames = mode === "video" && Boolean(activeGen?.supports_first_last_frame);
  const maxImages = modelLimit(activeGen, "max_images");
  const maxVideos = modelLimit(activeGen, "max_videos");
  const allowPhotos = maxImages > 0;
  const allowVideos = mode === "video" && maxVideos > 0;

  const loadModels = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/imagine/models`, {
        headers: authHeaders(token),
      });
      setConfigured(Boolean(data.configured));
      setModels(data.models || []);
    } catch (error) {
      toast.error(formatApiError(error, "Could not load Imagine models"));
    }
  }, [token]);

  const loadBackgroundLibrary = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/imagine/backgrounds`, {
        headers: authHeaders(token),
      });
      setBgLibrary(Array.isArray(data?.backgrounds) ? data.backgrounds : []);
    } catch (error) {
      toast.error(formatApiError(error, "Could not load saved backgrounds"));
    }
  }, [token]);

  const loadJobs = useCallback(async () => {
    if (!token || !vehicle?.id) return;
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/admin/vehicles/${vehicle.id}/imagine/jobs`,
        { headers: authHeaders(token) },
      );
      setJobs(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(formatApiError(error, "Could not load Imagine jobs"));
    }
  }, [token, vehicle?.id]);

  useEffect(() => {
    loadModels();
  }, [loadModels]);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  useEffect(() => {
    loadBackgroundLibrary();
  }, [loadBackgroundLibrary]);

  useEffect(() => {
    if (!genModels.length) return;
    if (!genModels.some((m) => m.id === genModel)) setGenModel(genModels[0].id);
  }, [genModels, genModel]);

  useEffect(() => {
    if (!editModels.length) return;
    if (!editModels.some((m) => m.id === fixModel)) setFixModel(editModels[0].id);
  }, [editModels, fixModel]);

  useEffect(() => {
    if (!imageModels.length) return;
    if (!imageModels.some((m) => m.id === bgModel)) {
      const preferred = imageModels.find((m) => m.id === "qwen-image-3-edit");
      setBgModel((preferred || imageModels[0]).id);
    }
  }, [imageModels, bgModel]);

  useEffect(() => {
    if (photos.length && !fixPhoto) setFixPhoto(photos[0].url);
  }, [photos, fixPhoto]);

  useEffect(() => {
    if (!activeGen?.id) return;
    setSelectedImages((cur) => (maxImages > 0 ? cur.slice(0, maxImages) : []));
    setSelectedVideos((cur) => (maxVideos > 0 ? cur.slice(0, maxVideos) : []));
  }, [activeGen?.id, maxImages, maxVideos]);

  useEffect(() => {
    if (!activeGen?.id) return;
    setFirstFrameUrl("");
    setLastFrameUrl("");
  }, [activeGen?.id]);

  const pending = jobs.some(isWorkingJob);
  useEffect(() => {
    if (!pending) return undefined;
    const id = setInterval(loadJobs, 3000);
    return () => clearInterval(id);
  }, [pending, loadJobs]);

  useEffect(() => {
    const total = Math.max(1, Math.ceil(jobs.length / resultsPageSize));
    if (resultsPage > total) setResultsPage(total);
  }, [jobs.length, resultsPage, resultsPageSize]);

  const toggleLimited = (current, setList, url, max, label) => {
    if (max <= 0) {
      toast.error(`This model does not use reference ${label}`);
      return;
    }
    if (current.includes(url)) {
      setList(current.filter((u) => u !== url));
      return;
    }
    if (max === 1) {
      setList([url]);
      return;
    }
    if (current.length >= max) {
      toast.error(`This model allows up to ${max} reference ${label}`);
      return;
    }
    setList([...current, url]);
  };

  const handleUploadRefs = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length || !token) return;
    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));
    setUploadingRefs(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/admin/imagine/upload`, formData, {
        headers: authHeaders(token),
      });
      const uploads = Array.isArray(data?.uploads) ? data.uploads : [];
      if (!uploads.length) {
        toast.error("No images were uploaded");
        return;
      }
      setUploadedRefs((cur) => {
        const seen = new Set(cur.map((item) => item.url));
        const next = [...cur];
        uploads.forEach((item) => {
          if (item?.url && !seen.has(item.url)) {
            seen.add(item.url);
            next.push(item);
          }
        });
        return next;
      });
      setSelectedImages((cur) => {
        const cap = Number(activeGen?.max_images) || 0;
        if (cap <= 0) return cur;
        const next = [...cur];
        uploads.forEach((item) => {
          if (!item?.url || next.includes(item.url) || next.length >= cap) return;
          next.push(item.url);
        });
        return next;
      });
      toast.success(uploads.length === 1 ? "Reference photo uploaded" : `${uploads.length} reference photos uploaded`);
    } catch (error) {
      toast.error(formatApiError(error, "Could not upload reference photos"));
    } finally {
      setUploadingRefs(false);
      if (uploadRef.current) uploadRef.current.value = "";
    }
  };

  const toggleBgPhoto = (url) => {
    setBgSelectedImages((cur) => {
      if (cur.includes(url)) return cur.filter((item) => item !== url);
      if (cur.length >= 12) {
        toast.error("Select up to 12 exterior photos at a time");
        return cur;
      }
      return [...cur, url];
    });
  };

  const handleUploadBackgrounds = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length || !token) return;
    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));
    setUploadingBg(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/imagine/upload?purpose=backgrounds`,
        formData,
        { headers: authHeaders(token) },
      );
      const uploads = Array.isArray(data?.uploads) ? data.uploads : [];
      if (!uploads.length) {
        toast.error("No background images were uploaded");
        return;
      }
      setBgUploads((cur) => {
        const seen = new Set(cur.map((item) => item.url));
        const next = [...cur];
        uploads.forEach((item) => {
          if (item?.url && !seen.has(item.url)) {
            seen.add(item.url);
            next.push(item);
          }
        });
        return next;
      });
      if (!bgBackgroundUrl && uploads[0]?.url) setBgBackgroundUrl(uploads[0].url);
      toast.success(uploads.length === 1 ? "Background uploaded" : `${uploads.length} backgrounds uploaded`);
    } catch (error) {
      toast.error(formatApiError(error, "Could not upload background images"));
    } finally {
      setUploadingBg(false);
      if (bgUploadRef.current) bgUploadRef.current.value = "";
    }
  };

  const handleUploadLibraryBackgrounds = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length || !token) return;
    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));
    setUploadingLibrary(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/admin/imagine/backgrounds`, formData, {
        headers: authHeaders(token),
      });
      const rows = Array.isArray(data?.backgrounds) ? data.backgrounds : [];
      if (!rows.length) {
        toast.error("No background images were saved");
        return;
      }
      setBgLibrary((cur) => {
        const seen = new Set(cur.map((item) => item.id || item.url));
        const next = [...rows.filter((item) => item?.url && !seen.has(item.id || item.url)), ...cur];
        return next;
      });
      toast.success(rows.length === 1 ? "Background saved to library" : `${rows.length} backgrounds saved to library`);
    } catch (error) {
      toast.error(formatApiError(error, "Could not save background images"));
    } finally {
      setUploadingLibrary(false);
      if (bgLibraryUploadRef.current) bgLibraryUploadRef.current.value = "";
    }
  };

  const deleteLibraryBackground = async (item) => {
    if (!item?.id || !token) return;
    setDeletingBgId(item.id);
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/imagine/backgrounds/${item.id}`, {
        headers: authHeaders(token),
      });
      setBgLibrary((cur) => cur.filter((row) => row.id !== item.id));
      if (bgBackgroundUrl === item.url) setBgBackgroundUrl("");
      toast.success("Background removed");
    } catch (error) {
      toast.error(formatApiError(error, "Could not delete background"));
    } finally {
      setDeletingBgId("");
    }
  };

  const submitChangeBackground = async () => {
    if (!vehicle?.id) return;
    if (!bgSelectedImages.length) {
      toast.error("Select at least one exterior listing photo");
      return;
    }
    if (!bgBackgroundUrl && !bgPrompt.trim()) {
      toast.error("Describe the background or select an uploaded background image");
      return;
    }
    setBgBusy(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/vehicles/${vehicle.id}/imagine/jobs/batch`,
        {
          model_id: activeBg?.id,
          prompt: bgBackgroundUrl ? "" : bgPrompt.trim(),
          image_urls: bgSelectedImages,
          background_image_url: bgBackgroundUrl || undefined,
        },
        { headers: authHeaders(token) },
      );
      const queued = Array.isArray(data?.jobs) ? data.jobs.length : 0;
      const failed = Array.isArray(data?.errors) ? data.errors.length : 0;
      if (queued && failed) {
        toast.success(`Queued ${queued} of ${queued + failed} background jobs`);
      } else if (queued) {
        toast.success(queued === 1 ? "Background job queued" : `${queued} background jobs queued`);
      }
      if (failed && data.errors[0]?.detail) {
        toast.error(data.errors[0].detail);
      }
      setResultsPage(1);
      await loadJobs();
    } catch (error) {
      toast.error(formatApiError(error, "Could not queue background jobs"));
    } finally {
      setBgBusy(false);
    }
  };

  const submitGenerate = async () => {
    if (!vehicle?.id) return;
    const neededPhotos = Number(activeGen?.min_images) || 0;
    const neededVideos = Number(activeGen?.min_videos) || 0;
    const photoCount = selectedImages.length + (
      firstFrameUrl && !selectedImages.includes(firstFrameUrl) ? 1 : 0
    ) + (
      lastFrameUrl && !selectedImages.includes(lastFrameUrl) && lastFrameUrl !== firstFrameUrl ? 1 : 0
    );
    if (neededPhotos && photoCount < neededPhotos) {
      toast.error(`This model needs at least ${neededPhotos} reference photo${neededPhotos === 1 ? "" : "s"}`);
      return;
    }
    if (neededVideos && selectedVideos.length < neededVideos) {
      toast.error(`This model needs at least ${neededVideos} reference video${neededVideos === 1 ? "" : "s"}`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/vehicles/${vehicle.id}/imagine/jobs`,
        {
          kind: mode === "video" ? "generate_video" : "generate_photo",
          model_id: activeGen?.id,
          prompt: genPrompt,
          image_urls: selectedImages,
          video_urls: selectedVideos,
          duration: mode === "video" ? Number(duration) || 5 : undefined,
          resolution: resolution || undefined,
          first_frame_url: supportsFrames ? firstFrameUrl || undefined : undefined,
          last_frame_url: supportsFrames ? lastFrameUrl || undefined : undefined,
        },
        { headers: authHeaders(token) },
      );
      toast.success("Imagine job submitted");
      if (data?.id) {
        setJobs((cur) => [data, ...cur.filter((job) => job.id !== data.id)]);
      }
      await loadJobs();
    } catch (error) {
      toast.error(formatApiError(error, "Could not start generation"));
    } finally {
      setBusy(false);
    }
  };

  const submitFix = async () => {
    if (!vehicle?.id || !fixPhoto) return;
    setFixBusy(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/vehicles/${vehicle.id}/imagine/jobs`,
        {
          kind: "fix_photo",
          model_id: fixModel,
          prompt: fixPrompt,
          image_urls: [fixPhoto],
          region_boxes: boxes,
        },
        { headers: authHeaders(token) },
      );
      toast.success("Photo fix submitted");
      if (data?.id) {
        setJobs((cur) => [data, ...cur.filter((job) => job.id !== data.id)]);
      }
      await loadJobs();
    } catch (error) {
      toast.error(formatApiError(error, "Could not start photo fix"));
    } finally {
      setFixBusy(false);
    }
  };

  const applyJob = async (job, action) => {
    setApplyingId(job.id);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/imagine/jobs/${job.id}/apply`,
        {
          action,
          replace_url: action === "replace" ? job.reference_image_urls?.[0] : undefined,
        },
        { headers: authHeaders(token) },
      );
      toast.success(
        action === "replace"
          ? "Replaced inventory photo"
          : action === "remove"
            ? "Removed from Media"
            : "Added to Media",
      );
      if (onApplied) await onApplied();
      await loadJobs();
    } catch (error) {
      toast.error(formatApiError(error, "Could not update inventory Media"));
    } finally {
      setApplyingId("");
    }
  };

  const downloadJob = async (job) => {
    const items = mediaItems(job).filter((item) => item?.url);
    if (!items.length) {
      toast.error("No generated media to download yet");
      return;
    }
    setDownloadingId(job.id);
    try {
      for (let index = 0; index < items.length; index += 1) {
        const item = items[index];
        await downloadUrl(item.url, downloadFilename(job, item, index, items.length));
      }
      toast.success(items.length === 1 ? "Download started" : `${items.length} files downloading`);
    } catch {
      toast.error("Could not download generated media");
    } finally {
      setDownloadingId("");
    }
  };

  const generateWorking = jobs.some(
    (job) => isWorkingJob(job) && (job.kind === "generate_video" || job.kind === "generate_photo"),
  );
  const fixWorking = jobs.some((job) => isWorkingJob(job) && job.kind === "fix_photo");
  const bgWorking = jobs.some((job) => isWorkingJob(job) && job.kind === "change_background");
  const resultsTotalPages = Math.max(1, Math.ceil(jobs.length / resultsPageSize));
  const resultsSafePage = Math.min(resultsPage, resultsTotalPages);
  const pagedJobs = jobs.slice(
    (resultsSafePage - 1) * resultsPageSize,
    resultsSafePage * resultsPageSize,
  );
  const infoRefs = infoJob ? jobReferencePreviewUrls(infoJob) : [];
  const infoGenerated = infoJob ? jobGeneratedParts(infoJob) : { name: "", role: "" };

  return (
    <section className="bg-white/5 border border-white/10 p-4 sm:p-5 self-start">
      <div className="mb-3">
        <h2 className="text-sm font-['Unbounded'] font-semibold text-white pt-1.5">Imagine</h2>
        <p className="mt-1 text-xs font-['Manrope'] text-white/45">
          Generate or fix listing photos and videos with Higgsfield. Results stay here until you add them to Media.
        </p>
      </div>
      {!configured && (
        <p className="text-sm font-['Manrope'] text-amber-300 mb-3" role="alert">
          Higgsfield keys are missing. Set HF_API_KEY_ID and HF_API_KEY_SECRET on the backend.
        </p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
        <div className="space-y-3">
          <h3 className="text-xs font-['Unbounded'] uppercase tracking-wide text-white/55">Generate</h3>
          <div>
            <label className={labelCls} htmlFor="imagine-mode">Output</label>
            <select
              id="imagine-mode"
              className={selectCls}
              value={mode}
              onChange={(e) => setMode(e.target.value)}
            >
              <option value="photo">Photo</option>
              <option value="video">Video</option>
            </select>
          </div>
          <div>
            <label className={labelCls} htmlFor="imagine-model">Model</label>
            <select
              id="imagine-model"
              className={selectCls}
              value={activeGen?.id || ""}
              onChange={(e) => setGenModel(e.target.value)}
            >
              {genModels.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <span className={labelCls}>Reference media</span>
            <div className="flex flex-wrap gap-3">
              {allowPhotos && referencePhotos.map((photo) => {
                const on = selectedImages.includes(photo.url);
                return (
                  <button
                    key={photo.public_id || photo.url}
                    type="button"
                    onClick={() => toggleLimited(selectedImages, setSelectedImages, photo.url, maxImages, maxImages === 1 ? "photo" : "photos")}
                    className={`${mediaThumbCls} ${
                      on ? "border-blue-400" : "border-white/15"
                    }`}
                  >
                    <img src={photo.url} alt="" className="w-full h-full object-cover" />
                  </button>
                );
              })}
              {allowVideos && videos.map((vid) => {
                const on = selectedVideos.includes(vid.url);
                return (
                  <button
                    key={vid.public_id || vid.url}
                    type="button"
                    onClick={() => toggleLimited(selectedVideos, setSelectedVideos, vid.url, maxVideos, maxVideos === 1 ? "video" : "videos")}
                    className={`${mediaThumbCls} text-[10px] font-['Manrope'] text-white ${
                      on ? "border-blue-400" : "border-white/15"
                    }`}
                  >
                    {vid.thumbnail_url ? (
                      <img src={vid.thumbnail_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="flex items-center justify-center h-full bg-black/50">Video</span>
                    )}
                  </button>
                );
              })}
              {allowPhotos && (
                <>
                  <input
                    ref={uploadRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                    multiple={maxImages !== 1}
                    className="hidden"
                    onChange={(e) => handleUploadRefs(e.target.files)}
                  />
                  <button
                    type="button"
                    disabled={uploadingRefs || !configured}
                    onClick={() => uploadRef.current?.click()}
                    className={`${mediaTileCls} border-dashed border-white/25`}
                  >
                    {uploadingRefs ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <ImagePlus className="w-5 h-5" />
                    )}
                    <span className="text-[9px] font-['Manrope'] leading-tight px-1">Upload</span>
                  </button>
                </>
              )}
              {allowPhotos && !referencePhotos.length && !allowVideos && (
                <p className="text-sm font-['Manrope'] text-white/40 self-center">
                  Select inventory photos or upload from this device.
                </p>
              )}
            </div>
            <p className="mt-1 text-[11px] font-['Manrope'] text-white/40">
              {referenceLimitHint(activeGen, mode)}
              {allowPhotos ? ` ${selectedImages.length}/${maxImages} photos` : ""}
              {allowVideos ? ` · ${selectedVideos.length}/${maxVideos} videos` : ""}
            </p>
          </div>
          <div>
            <label className={labelCls} htmlFor="imagine-prompt">Prompt</label>
            <textarea
              id="imagine-prompt"
              className={`${inputCls} min-h-[96px]`}
              value={genPrompt}
              onChange={(e) => setGenPrompt(e.target.value)}
              placeholder="Describe the still or clip to generate from the selected references"
            />
          </div>
          {supportsFrames && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <PhotoPreviewPicker
                id="imagine-first-frame"
                label="First frame"
                photos={referencePhotos}
                value={firstFrameUrl}
                onChange={setFirstFrameUrl}
                disabled={busy}
                allowEmpty
                emptyLabel="None"
              />
              <PhotoPreviewPicker
                id="imagine-last-frame"
                label="Last frame"
                photos={referencePhotos}
                value={lastFrameUrl}
                onChange={setLastFrameUrl}
                disabled={busy}
                allowEmpty
                emptyLabel="None"
              />
            </div>
          )}
          {mode === "video" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls} htmlFor="imagine-duration">Duration (sec)</label>
                <input
                  id="imagine-duration"
                  type="number"
                  min={4}
                  max={30}
                  className={inputCls}
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                />
              </div>
              <div>
                <label className={labelCls} htmlFor="imagine-res">Resolution</label>
                <select
                  id="imagine-res"
                  className={selectCls}
                  value={resolution}
                  onChange={(e) => setResolution(e.target.value)}
                >
                  <option value="">Model default</option>
                  <option value="480p">480p</option>
                  <option value="720p">720p</option>
                  <option value="1080p">1080p</option>
                </select>
              </div>
            </div>
          )}
          <button
            type="button"
            disabled={busy || !configured}
            onClick={submitGenerate}
            className="min-h-11 px-4 inline-flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-['Manrope'] font-semibold disabled:opacity-50"
          >
            {(busy || generateWorking) && <Loader2 className="w-4 h-4 animate-spin" />}
            {busy ? "Submitting…" : generateWorking ? "Generating…" : "Generate"}
          </button>
          {generateWorking && (
            <p className="text-xs font-['Manrope'] text-white/50 flex items-center gap-2" role="status">
              Higgsfield is generating. A preview will appear in Results when it finishes.
            </p>
          )}
        </div>

        <div className="space-y-3">
          <h3 className="text-xs font-['Unbounded'] uppercase tracking-wide text-white/55">
            Fix current photo
          </h3>
          <PhotoPreviewPicker
            id="imagine-fix-photo"
            label="Photo"
            photos={photos}
            value={fixPhoto}
            onChange={(url) => {
              setFixPhoto(url);
              setBoxes([]);
            }}
            disabled={fixBusy}
          />
          <ImagineRegionEditor src={fixPhoto} boxes={boxes} onChange={setBoxes} disabled={fixBusy} />
          <div>
            <label className={labelCls} htmlFor="imagine-fix-model">Edit model</label>
            <select
              id="imagine-fix-model"
              className={selectCls}
              value={fixModel}
              onChange={(e) => setFixModel(e.target.value)}
            >
              {editModels.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls} htmlFor="imagine-fix-prompt">Fix prompt</label>
            <textarea
              id="imagine-fix-prompt"
              className={`${inputCls} min-h-[80px]`}
              value={fixPrompt}
              onChange={(e) => setFixPrompt(e.target.value)}
              placeholder="Example: remove windshield glare, keep the rest of the car unchanged"
            />
          </div>
          <button
            type="button"
            disabled={fixBusy || !configured || !fixPhoto}
            onClick={submitFix}
            className="min-h-11 px-4 inline-flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-['Manrope'] font-semibold disabled:opacity-50"
          >
            {(fixBusy || fixWorking) && <Loader2 className="w-4 h-4 animate-spin" />}
            {fixBusy ? "Submitting…" : fixWorking ? "Generating fix…" : "Generate fix"}
          </button>
          {fixWorking && (
            <p className="text-xs font-['Manrope'] text-white/50 flex items-center gap-2" role="status">
              Photo fix is in progress. The result will appear below when Higgsfield finishes.
            </p>
          )}
        </div>
      </div>

      <div className="mt-6 pt-4 border-t border-white/10 space-y-3">
        <h3 className="text-xs font-['Unbounded'] uppercase tracking-wide text-white/55">Change Background</h3>
        <p className="text-xs font-['Manrope'] text-white/45">
          Replace the scene behind selected listing photos. Describe a background, or upload a scene image and use that instead.
        </p>
        <div>
          <label className={labelCls} htmlFor="imagine-bg-model">Model</label>
          <select
            id="imagine-bg-model"
            className={selectCls}
            value={activeBg?.id || ""}
            onChange={(e) => setBgModel(e.target.value)}
          >
            {imageModels.map((m) => (
              <option key={m.id} value={m.id}>
                {m.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <span className={labelCls}>Exterior photos</span>
          <div className="flex flex-wrap gap-3">
            {photos.map((photo) => {
              const on = bgSelectedImages.includes(photo.url);
              return (
                <button
                  key={`bg-${photo.public_id || photo.url}`}
                  type="button"
                  onClick={() => toggleBgPhoto(photo.url)}
                  className={`${mediaThumbCls} ${
                    on ? "border-blue-400" : "border-white/15"
                  }`}
                >
                  <img src={photo.url} alt="" className="w-full h-full object-cover" />
                </button>
              );
            })}
            {!photos.length && (
              <p className="text-sm font-['Manrope'] text-white/40 self-center">
                Add listing photos in Media first.
              </p>
            )}
          </div>
          {bgSelectedImages.length > 0 && (
            <p className="mt-2 text-xs font-['Manrope'] text-amber-200" role="note">
              Use exterior photos only for background change. Interior, engine bay, odometer, and close-up detail shots will not look right.
            </p>
          )}
          <p className="mt-1 text-[11px] font-['Manrope'] text-white/40">
            {bgSelectedImages.length}/12 photos · each photo is queued as its own job
          </p>
        </div>
        <div>
          <label className={labelCls} htmlFor="imagine-bg-prompt">
            Enter a prompt describing what kind of background needs to be generated.
          </label>
          <textarea
            id="imagine-bg-prompt"
            className={`${inputCls} min-h-[80px]`}
            value={bgPrompt}
            onChange={(e) => setBgPrompt(e.target.value)}
            disabled={Boolean(bgBackgroundUrl)}
            placeholder={
              bgBackgroundUrl
                ? "An uploaded background is selected. The server will place the car into that scene."
                : "Example: clean white photo studio with soft even lighting"
            }
          />
          {bgBackgroundUrl && (
            <p className="mt-1 text-[11px] font-['Manrope'] text-white/40">
              Prompt is skipped while a background image is selected. Click the selected scene again to clear it and type a prompt instead.
            </p>
          )}
        </div>
        <div>
          <span className={labelCls}>Uploaded background</span>
          <div className="flex flex-wrap gap-3">
            {bgBackgroundUrl && !bgUploads.some((item) => item.url === bgBackgroundUrl) && (
              <button
                type="button"
                onClick={() => setBgBackgroundUrl("")}
                className={`${mediaThumbCls} border-blue-400`}
                title="Clear selected background"
              >
                <img src={bgBackgroundUrl} alt="" className="w-full h-full object-cover" />
              </button>
            )}
            {bgUploads.map((item) => {
              const on = bgBackgroundUrl === item.url;
              return (
                <button
                  key={item.public_id || item.url}
                  type="button"
                  onClick={() => setBgBackgroundUrl((cur) => (cur === item.url ? "" : item.url))}
                  className={`${mediaThumbCls} ${
                    on ? "border-blue-400" : "border-white/15"
                  }`}
                >
                  <img src={item.url} alt="" className="w-full h-full object-cover" />
                </button>
              );
            })}
            <input
              ref={bgUploadRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
              multiple
              className="hidden"
              onChange={(e) => handleUploadBackgrounds(e.target.files)}
            />
            <button
              type="button"
              disabled={!configured}
              onClick={() => setBgLibraryOpen(true)}
              className={`${mediaTileCls} border-white/25`}
              data-testid="imagine-bg-select"
            >
              <File className="w-5 h-5" />
              <span className="text-[11px] font-['Manrope'] leading-tight px-1">Select</span>
            </button>
            <button
              type="button"
              disabled={uploadingBg || !configured}
              onClick={() => bgUploadRef.current?.click()}
              className={`${mediaTileCls} border-dashed border-white/25`}
            >
              {uploadingBg ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImagePlus className="w-5 h-5" />}
              <span className="text-[11px] font-['Manrope'] leading-tight px-1">Upload</span>
            </button>
          </div>
          <p className="mt-1 text-[11px] font-['Manrope'] text-white/40">
            Select opens saved backgrounds. Upload here is for this session only.
          </p>
        </div>
        <button
          type="button"
          disabled={
            bgBusy
            || !configured
            || !bgSelectedImages.length
            || (!bgBackgroundUrl && !bgPrompt.trim())
          }
          onClick={submitChangeBackground}
          className="min-h-11 px-4 inline-flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-['Manrope'] font-semibold disabled:opacity-50"
        >
          {(bgBusy || bgWorking) && <Loader2 className="w-4 h-4 animate-spin" />}
          {bgBusy ? "Submitting…" : bgWorking ? "Changing background…" : "Change background"}
        </button>
        {bgWorking && (
          <p className="text-xs font-['Manrope'] text-white/50 flex items-center gap-2" role="status">
            Background jobs are running. Previews appear in Results when Higgsfield finishes.
          </p>
        )}
      </div>

      <div className="mt-6 pt-4 border-t border-white/10 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-xs font-['Unbounded'] uppercase tracking-wide text-white/55">Results</h3>
          {pending && (
            <span className="inline-flex items-center gap-1.5 text-xs font-['Manrope'] text-blue-300" role="status">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Generation in progress
            </span>
          )}
          {jobs.length > 0 && (
            <select
              className={`${selectCls} w-auto ml-auto`}
              value={resultsPageSize}
              onChange={(e) => {
                setResultsPageSize(Number(e.target.value));
                setResultsPage(1);
              }}
              data-testid="imagine-results-page-size"
              aria-label="Results per page"
            >
              {RESULTS_PAGE_SIZES.map((n) => (
                <option key={n} value={n}>
                  {n} per page
                </option>
              ))}
            </select>
          )}
        </div>
        {!jobs.length && (
          <p className="text-sm font-['Manrope'] text-white/40">No Imagine jobs for this vehicle yet.</p>
        )}
        {pagedJobs.map((job) => {
          const media = mediaItems(job);
          const working = isWorkingJob(job);
          const timedOut = isTimeoutJob(job);
          const hasVideo = media.some(isVideoMedia);
          const inMedia = jobInMedia(job, vehicle);
          return (
            <div
              key={job.id}
              className="border border-white/10 bg-black/20 p-3 space-y-2"
              aria-busy={working}
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-['Manrope'] text-white">
                  {timedOut && !media.length ? "Checking Higgsfield" : statusLabel(job.status)}
                  <span className="text-white/45"> · {jobKindLabel(job)} · {job.model_id}</span>
                </p>
                {inMedia && (
                  <span className="text-xs font-['Manrope'] text-emerald-300">Added to Media</span>
                )}
              </div>
              {job.prompt && (
                <p className="text-xs font-['Manrope'] text-white/55 whitespace-pre-wrap">{job.prompt}</p>
              )}
              {timedOut && (
                <p className="text-sm font-['Manrope'] text-amber-300">
                  Still checking Higgsfield for a finished result. The dashboard may already show this job as complete.
                </p>
              )}
              {job.error && !timedOut && (
                <p className="text-sm font-['Manrope'] text-red-400">{job.error}</p>
              )}
              {working && !media.length && (
                <div
                  className="flex flex-col items-center justify-center gap-3 w-full max-w-xl min-h-[220px] bg-black/50 border border-white/10"
                  role="status"
                  aria-live="polite"
                >
                  <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
                  <p className="text-sm font-['Manrope'] text-white/80">{workingLabel(job)}</p>
                  <p className="text-xs font-['Manrope'] text-white/40 px-4 text-center">
                    Higgsfield is still working. Video jobs can take several minutes.
                  </p>
                </div>
              )}
              {media.map((item) => (
                isVideoMedia(item) ? (
                  <video
                    key={item.url}
                    src={item.url}
                    controls
                    playsInline
                    preload="metadata"
                    poster={item.thumbnail_url}
                    className="w-full max-w-xl bg-black"
                  />
                ) : (
                  <img
                    key={item.url}
                    src={item.url}
                    alt="Generated Imagine result"
                    className="w-full max-w-xl object-contain bg-black/40"
                  />
                )
              ))}
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  type="button"
                  onClick={() => setInfoJob(job)}
                  className="min-h-11 px-4 border border-white/15 text-white font-['Manrope'] hover:bg-white/5"
                >
                  View More Info
                </button>
                {media.length > 0 && (
                  <button
                    type="button"
                    disabled={downloadingId === job.id}
                    onClick={() => downloadJob(job)}
                    className="min-h-11 px-4 inline-flex items-center justify-center gap-2 border border-white/15 text-white font-['Manrope'] hover:bg-white/5 disabled:opacity-50"
                  >
                    {downloadingId === job.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                    {downloadingId === job.id ? "Downloading…" : "Download"}
                  </button>
                )}
                {job.status === "completed" && media.length > 0 && (
                  <>
                    <button
                      type="button"
                      disabled={applyingId === job.id}
                      onClick={() => applyJob(job, inMedia ? "remove" : "add")}
                      className={`min-h-11 px-4 font-['Manrope'] font-semibold disabled:opacity-50 ${
                        inMedia
                          ? "border border-white/15 text-white hover:bg-white/5"
                          : "bg-blue-500 hover:bg-blue-600 text-white"
                      }`}
                    >
                      {applyingId === job.id
                        ? (inMedia ? "Removing…" : "Adding…")
                        : inMedia
                          ? "Remove from Media"
                          : "Add to Media"}
                    </button>
                    {!inMedia && !hasVideo && job.kind === "fix_photo" && job.reference_image_urls?.[0] && (
                      <button
                        type="button"
                        disabled={applyingId === job.id}
                        onClick={() => applyJob(job, "replace")}
                        className="min-h-11 px-4 border border-white/15 text-white font-['Manrope'] hover:bg-white/5 disabled:opacity-50"
                      >
                        Replace original photo
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
        {jobs.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
            <p className="text-sm font-['Manrope'] text-white/50">
              Showing {pagedJobs.length} of {jobs.length} · Page {resultsSafePage} of {resultsTotalPages}
            </p>
            {resultsTotalPages > 1 && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={resultsSafePage <= 1}
                  onClick={() => setResultsPage((p) => Math.max(1, p - 1))}
                  className="min-h-11 px-3 py-1.5 border border-white/10 text-white/70 text-sm font-['Manrope'] disabled:opacity-40"
                >
                  Previous
                </button>
                {Array.from({ length: resultsTotalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    type="button"
                    onClick={() => setResultsPage(page)}
                    className={`min-w-[2.25rem] min-h-11 px-2 py-1.5 text-sm font-['Manrope'] ${
                      page === resultsSafePage
                        ? "bg-blue-500 text-white"
                        : "border border-white/10 text-white/70 hover:bg-white/5"
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={resultsSafePage >= resultsTotalPages}
                  onClick={() => setResultsPage((p) => Math.min(resultsTotalPages, p + 1))}
                  className="min-h-11 px-3 py-1.5 border border-white/10 text-white/70 text-sm font-['Manrope'] disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <AdminDialog
        open={bgLibraryOpen}
        onClose={() => setBgLibraryOpen(false)}
        title="Select background"
        maxWidth="max-w-3xl"
        testId="imagine-bg-library"
        footer={(
          <div className="flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setBgLibraryOpen(false)}
              className="min-h-11 px-4 border border-white/15 text-white/80 font-['Manrope']"
            >
              Close
            </button>
          </div>
        )}
      >
        <p className="text-sm font-['Manrope'] text-white/55 mb-4">
          Backgrounds uploaded here stay available for every vehicle and session until you delete them.
        </p>
        <div className="flex flex-wrap gap-3 mb-4">
          {bgLibrary.map((item) => {
            const on = bgBackgroundUrl === item.url;
            return (
              <div key={item.id || item.url} className="relative">
                <button
                  type="button"
                  onClick={() => {
                    setBgBackgroundUrl(item.url);
                    setBgLibraryOpen(false);
                  }}
                  className={`${libraryThumbCls} ${
                    on ? "border-blue-400" : "border-white/15"
                  }`}
                >
                  <img src={item.url} alt="" className="w-full h-full object-cover" />
                </button>
                <button
                  type="button"
                  aria-label="Delete background"
                  disabled={deletingBgId === item.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteLibraryBackground(item);
                  }}
                  className="absolute top-1 right-1 min-h-8 min-w-8 inline-flex items-center justify-center bg-black/70 text-white/80 hover:text-red-300"
                >
                  {deletingBgId === item.id ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Trash2 className="w-3.5 h-3.5" />
                  )}
                </button>
              </div>
            );
          })}
          <input
            ref={bgLibraryUploadRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
            multiple
            className="hidden"
            onChange={(e) => handleUploadLibraryBackgrounds(e.target.files)}
          />
          <button
            type="button"
            disabled={uploadingLibrary || !configured}
            onClick={() => bgLibraryUploadRef.current?.click()}
            className={libraryTileCls}
          >
            {uploadingLibrary ? <Loader2 className="w-6 h-6 animate-spin" /> : <ImagePlus className="w-6 h-6" />}
            <span className="text-[10px] font-['Manrope'] leading-tight px-1">Upload</span>
          </button>
        </div>
        {!bgLibrary.length && (
          <p className="text-sm font-['Manrope'] text-white/40">
            No saved backgrounds yet. Upload a scene to keep it for later sessions.
          </p>
        )}
      </AdminDialog>

      <AdminDialog
        open={Boolean(infoJob)}
        onClose={() => setInfoJob(null)}
        title="Generation info"
        maxWidth="max-w-lg"
        testId="imagine-job-info"
      >
        {infoJob && (
          <dl className="space-y-4 font-['Manrope']">
            <div>
              <dt className="text-xs uppercase tracking-wide text-white/50">Approximate cost for Generation</dt>
              <dd className="mt-1 text-sm text-white">{formatJobCost(infoJob)}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-white/50">Generated on</dt>
              <dd className="mt-1 text-sm text-white">{formatDateTimeMdY(infoJob.created_at)}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-white/50">Generated by</dt>
              <dd className="mt-1 text-sm text-white">
                <span className="block">{infoGenerated.name}</span>
                {infoGenerated.role ? (
                  <span className="block text-white/60 mt-0.5">{infoGenerated.role}</span>
                ) : null}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-white/50">Reference Images</dt>
              <dd className="mt-2">
                {infoRefs.length ? (
                  <div className="flex flex-wrap gap-2">
                    {infoRefs.map((url) => (
                      <img
                        key={url}
                        src={url}
                        alt="Reference used for this generation"
                        className="w-20 h-20 object-cover bg-black/40 border border-white/10"
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-white/55">None</p>
                )}
              </dd>
            </div>
          </dl>
        )}
      </AdminDialog>
    </section>
  );
}
